import { useMemo } from 'react';
import Link from '@/components/compat/Link';
import { ArrowRight } from 'lucide-react';
import {
  type PowerInfo,
  getRelatedPowers,
  getRelatedExploreTitle,
  getPowerCategoryLabel,
  formatFairtradePrice,
} from '@/lib/powers-data';
import { applyFairtradePricesToList } from '@/lib/fairtrade-data';
import {
  useFairtradePrices,
  useCatalogPowers,
} from '@/components/painel/powers/powers-cms-context';
import { getPowerCategoryBadgeStyle } from '@/lib/power-category-theme';
import { GsImage, XatsPriceValue } from '@/components/help-center/latest-power-shared';
import PowerDarkCardShell from '@/components/help-center/powers/PowerDarkCardShell';
import { PowerSectionSmileyIcon } from '@/components/help-center/powers/power-gallery-view';

function RelatedCard({ power }: { power: PowerInfo }) {
  const categoryBadgeStyle = getPowerCategoryBadgeStyle(power.category);

  return (
    <Link href={`/powers/${power.slug}`} className="group block">
      <PowerDarkCardShell
        className="h-full transition-all duration-300 hover:border-[rgba(0,11,106,0.55)] hover:ring-[rgba(0,11,106,0.35)] hover:-translate-y-1 hover:shadow-blue-950/50"
        innerClassName="flex items-center gap-4 p-4"
      >
        <div
          className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl"
          style={{
            background:
              'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
            boxShadow: '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70',
          }}
        >
          <GsImage
            code={power.slug}
            className="flex h-full w-full items-center justify-center"
            imgClassName="max-h-[44px] max-w-[44px] object-contain"
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h4 className="font-poppins font-bold text-white text-sm group-hover:text-blue-100 transition-colors truncate">
              {power.name}
            </h4>
            <span
              className="hidden sm:inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 font-poppins text-[9px] font-bold uppercase tracking-wide"
              style={categoryBadgeStyle}
            >
              {getPowerCategoryLabel(power.category)}
            </span>
          </div>
          <p className="text-[11px] text-blue-100/70 font-inter leading-snug line-clamp-1 truncate">
            {power.description.short}
          </p>
          <XatsPriceValue className="mt-0.5 text-[11px] text-white">
            {formatFairtradePrice(power.prices.fairtrade_min, power.prices.fairtrade_max)}
          </XatsPriceValue>
        </div>

        <ArrowRight className="h-4 w-4 text-blue-100/30 group-hover:text-sky-200 group-hover:translate-x-0.5 transition-all shrink-0" />
      </PowerDarkCardShell>
    </Link>
  );
}

export default function PowerDetailRelated({ power }: { power: PowerInfo }) {
  const categoryLabel = getPowerCategoryLabel(power.category);
  const categoryBadgeStyle = getPowerCategoryBadgeStyle(power.category);
  const sectionTitle = getRelatedExploreTitle(power);
  const { prices: fairtradePrices } = useFairtradePrices();
  const { powers: catalogPowers } = useCatalogPowers();

  const related = useMemo(() => {
    const list = getRelatedPowers(power, 4, catalogPowers);
    if (!fairtradePrices) return list;
    return applyFairtradePricesToList(list, fairtradePrices);
  }, [power, fairtradePrices, catalogPowers]);

  if (related.length === 0) return null;

  return (
    <section className="bg-gray-50 py-14 border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 gap-4">
          <div className="flex items-center">
            <PowerSectionSmileyIcon code="sherlock" size="xl" />
            <div className="-ml-1">
              <span
                className="mb-1.5 inline-flex items-center rounded-full border px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-widest"
                style={categoryBadgeStyle}
              >
                {categoryLabel}
              </span>
              <h2 className="text-xl font-bold text-gray-900 font-poppins">{sectionTitle}</h2>
            </div>
          </div>
          <Link
            href={`/powers?category=${power.category}`}
            className="inline-flex shrink-0 items-center justify-center gap-2 self-start sm:self-auto rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 font-poppins shadow-sm transition-all hover:border-[rgba(0,11,106,0.28)] hover:bg-[rgba(0,11,106,0.05)] hover:text-[#000b6a] hover:shadow-md"
          >
            Ver todos
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {related.map((p) => (
            <RelatedCard key={p.slug} power={p} />
          ))}
        </div>
      </div>
    </section>
  );
}
